import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../app/store";

export const SKILLS_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

export interface SkillListItem {
  _id: string;
  id?: string;
  name?: string;
  name_ar?: string;
  status?: boolean;
  createdAt?: string;
  [key: string]: unknown;
}

export interface SkillsState {
  list: SkillListItem[];
  lastFetchedAt: number | null;
}

const initialState: SkillsState = {
  list: [],
  lastFetchedAt: null,
};

export const skillsSlice = createSlice({
  name: "skills",
  initialState,
  reducers: {
    setSkills: (
      state,
      action: PayloadAction<{ list: SkillListItem[]; lastFetchedAt: number }>
    ) => {
      state.list = action.payload.list;
      state.lastFetchedAt = action.payload.lastFetchedAt;
    },
    addSkill: (state, action: PayloadAction<SkillListItem>) => {
      const item = {
        ...action.payload,
        id: action.payload._id ?? action.payload.id,
      };
      state.list.push(item);
    },
    updateSkill: (
      state,
      action: PayloadAction<{ id: string; data: Partial<SkillListItem> }>
    ) => {
      const index = state.list.findIndex((s) => s._id === action.payload.id);
      if (index !== -1) {
        state.list[index] = {
          ...state.list[index],
          ...action.payload.data,
          _id: action.payload.id,
          id: action.payload.id,
        };
      }
    },
    removeSkill: (state, action: PayloadAction<string>) => {
      state.list = state.list.filter((s) => s._id !== action.payload);
    },
    clearSkills: (state) => {
      state.list = [];
      state.lastFetchedAt = null;
    },
  },
});

export const {
  setSkills,
  addSkill,
  updateSkill,
  removeSkill,
  clearSkills,
} = skillsSlice.actions;

export const selectSkillsList = (state: RootState) => state.skills.list;
export const selectSkillsLastFetchedAt = (state: RootState) =>
  state.skills.lastFetchedAt;
export const selectIsSkillsCacheValid = (state: RootState): boolean => {
  const last = state.skills.lastFetchedAt;
  if (last == null) return false;
  return Date.now() - last < SKILLS_CACHE_TTL_MS;
};

export default skillsSlice.reducer;
